import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { M15_PAYMENT_MODES } from '../../../../shared/contracts/backend-enums.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** An approved voucher awaiting payment (GET /v1/payments/payment-entry). */
interface PayableVoucher {
  readonly proposalId: string;
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly proposalAmount: number;
  readonly tdsAmount: number;
  readonly otherDeductions: number;
  readonly netAmount: number;
  readonly beneficiaryName: string;
  readonly beneficiaryAccountNo: string | null;
  readonly beneficiaryIfsc: string | null;
}

interface Payable {
  readonly bankDetailsRequired: boolean;
  readonly vouchers: readonly PayableVoucher[];
}

interface Entered {
  readonly advice: { readonly adviceNumber: string };
}

/**
 * L15.15 — Payment Entry (owner, 30 Oct: "Sales Accounts enters the payment").
 * The approved vouchers awaiting payment are listed; Sales Accounts picks one
 * and enters the day it was paid, the mode and the bank's reference. Whether
 * the bank details are required is the tenant's setting; the beneficiary is
 * the one the voucher took from the vendor master and is never typed. Entering
 * pays the bill and issues the vendor's payment advice.
 */
export function PaymentEntryPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [data, setData] = useState<Payable | null>(null);
  const [selected, setSelected] = useState<PayableVoucher | null>(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [mode, setMode] = useState<string>(M15_PAYMENT_MODES[0]);
  const [utrNumber, setUtrNumber] = useState('');
  const [bankReferenceNo, setBankReferenceNo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiRequest<Payable>('/v1/payments/payment-entry', { token })
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const pick = (v: PayableVoucher) => {
    setSelected(v);
    setMode(M15_PAYMENT_MODES[0]);
    setPaymentDate(''); setUtrNumber(''); setBankReferenceNo('');
    setError(null); setNotice(null);
  };

  const bankRequired = data?.bankDetailsRequired ?? true;
  const blocker = !paymentDate ? t('finance.entry.needDate')
    : bankRequired && !utrNumber.trim() && !bankReferenceNo.trim() ? t('finance.entry.needReference')
    : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || blocker) return;
    setBusy(true); setError(null);
    try {
      const r = await apiRequest<Entered>(`/v1/payments/payment-entry/${selected.proposalId}`, {
        token, method: 'POST',
        body: {
          paymentDate, paymentModeCode: mode,
          utrNumber: utrNumber.trim() || undefined,
          bankReferenceNo: bankReferenceNo.trim() || undefined,
        },
      });
      setNotice(t('finance.entry.entered', { advice: r.advice.adviceNumber }));
      setSelected(null);
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const cell = { padding: 8 } as const;
  return (
    <div style={{ padding: 24 }}>
      <h1>{t('finance.entry.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)' }}>
        {bankRequired ? t('finance.entry.bankRequired') : t('finance.entry.bankOptional')}
      </p>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {notice && <p role="status" style={{ color: 'var(--color-success)' }}>{notice}</p>}
      {!data && !error && <p>{t('finance.entry.loading')}</p>}
      {data && data.vouchers.length === 0 && <p data-testid="entry-empty">{t('finance.entry.empty')}</p>}
      {data && data.vouchers.length > 0 && (
        <table aria-label="approved-vouchers" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-muted)', borderBottom: '1px solid var(--color-border-default)' }}>
              <th style={{ ...cell, textAlign: 'left' }}>{t('finance.entry.colBill')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('finance.entry.colVendor')}</th>
              <th style={{ ...cell, textAlign: 'right' }}>{t('finance.entry.colGross')}</th>
              <th style={{ ...cell, textAlign: 'right' }}>{t('finance.entry.colTds')}</th>
              <th style={{ ...cell, textAlign: 'right' }}>{t('finance.entry.colNet')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('finance.entry.colBank')}</th>
              <th style={cell}>{t('finance.entry.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {data.vouchers.map((v) => (
              <tr key={v.proposalId} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={cell}>{billRefLabel(v.bill, day)}</td>
                <td style={cell}>{personLabel(v.vendorName, v.vendorCode) ?? '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(v.proposalAmount)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(v.tdsAmount)}</td>
                <td style={{ ...cell, textAlign: 'right' }} data-testid={`net-${v.proposalId}`}>{money(v.netAmount)}</td>
                <td style={cell}>
                  {v.beneficiaryName}
                  {v.beneficiaryAccountNo ? ` · ${v.beneficiaryAccountNo} · ${v.beneficiaryIfsc ?? ''}` : ''}
                </td>
                <td style={{ ...cell, textAlign: 'center' }}>
                  <button type="button" data-testid={`pay-${v.proposalId}`} onClick={() => pick(v)}>{t('finance.entry.enter')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <form aria-label="payment-entry" onSubmit={(e) => void submit(e)}
          style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>
            {t('finance.entry.paying', { vendor: selected.beneficiaryName, amount: money(selected.netAmount) })}
          </h2>
          <label>{t('finance.entry.paymentDate')}
            <DateInput aria-label="payment-date" type="date" value={paymentDate} onValueChange={setPaymentDate} required />
          </label>
          <label>{t('finance.entry.mode')}
            <select aria-label="payment-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
              {M15_PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>{t('finance.entry.utr')}
            <input aria-label="utr" value={utrNumber} maxLength={30} onChange={(e) => setUtrNumber(e.target.value)} />
          </label>
          <label>{t('finance.entry.bankReference')}
            <input aria-label="bank-reference" value={bankReferenceNo} maxLength={50} onChange={(e) => setBankReferenceNo(e.target.value)} />
          </label>
          {blocker && <p role="status" aria-label="entry-blocked-reason">{blocker}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy || !!blocker}>{busy ? t('finance.entry.saving') : t('finance.entry.save')}</button>
            <button type="button" onClick={() => setSelected(null)}>{t('finance.entry.cancel')}</button>
          </div>
        </form>
      )}
    </div>
  );
}
